import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Car, Hash, Gauge } from "lucide-react";
import { EditableField } from "./EditableField";

/**
 * Vehicle Information Section for Rental Agreement
 */
export const VehicleInfoSection = ({ 
  vehicleInfo, 
  setVehicleInfo, 
  isEditing,
  fleetVehicles = [],
  selectedVehicleId,
  onSelectFleetVehicle
}) => {
  return (
    <Card>
      <CardHeader className="pb-4">
        <CardTitle className="text-sm font-semibold flex items-center gap-2">
          <Car className="w-4 h-4 text-primary" />
          VEHICLE INFORMATION
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Fleet Vehicle Selector */}
        {isEditing && fleetVehicles.length > 0 && (
          <div className="mb-4 p-3 bg-muted/50 rounded-lg border border-dashed">
            <Label className="text-xs text-muted-foreground uppercase mb-2 block">
              Select from Fleet
            </Label>
            <Select 
              value={selectedVehicleId || "none"} 
              onValueChange={(v) => onSelectFleetVehicle(v === "none" ? "" : v)}
            >
              <SelectTrigger className="h-9" data-testid="fleet-vehicle-selector">
                <SelectValue placeholder="Choose a vehicle from your fleet..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">-- Manual Entry --</SelectItem>
                {fleetVehicles.map((v) => (
                  <SelectItem key={v.id} value={v.id}>
                    {v.year} {v.make} {v.model} - {v.licensePlate} ({v.status})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedVehicleId && (
              <p className="text-xs text-muted-foreground mt-2">
                Vehicle ID: <span className="font-mono">{selectedVehicleId}</span>
              </p>
            )}
          </div>
        )}
        {!isEditing && selectedVehicleId && (
          <div className="mb-3 p-2 bg-primary/5 rounded border border-primary/20">
            <p className="text-xs text-primary">
              Fleet Vehicle: <span className="font-mono">{selectedVehicleId}</span>
            </p>
          </div>
        )}
        
        {/* Vehicle Details */}
        {isEditing ? (
          <div className="flex gap-2">
            <Input 
              value={vehicleInfo.year || ''} 
              onChange={(e) => setVehicleInfo({...vehicleInfo, year: e.target.value})} 
              placeholder="Year" 
              className="h-8 text-sm w-20" 
            />
            <Input 
              value={vehicleInfo.make || ''} 
              onChange={(e) => setVehicleInfo({...vehicleInfo, make: e.target.value})} 
              placeholder="Make" 
              className="h-8 text-sm" 
            />
            <Input 
              value={vehicleInfo.model || ''} 
              onChange={(e) => setVehicleInfo({...vehicleInfo, model: e.target.value})} 
              placeholder="Model" 
              className="h-8 text-sm" 
            />
          </div>
        ) : (
          <p className="text-sm font-medium">
            {vehicleInfo.year} {vehicleInfo.make} {vehicleInfo.model}
          </p>
        )}
        
        <EditableField 
          label="Color" 
          value={vehicleInfo.color} 
          isEditing={isEditing}
          onChange={(v) => setVehicleInfo({...vehicleInfo, color: v})} 
        />
        <EditableField 
          label="License Plate" 
          value={vehicleInfo.licensePlate} 
          isEditing={isEditing}
          onChange={(v) => setVehicleInfo({...vehicleInfo, licensePlate: v})} 
          icon={<Hash className="w-3.5 h-3.5" />} 
        />
        <EditableField 
          label="VIN" 
          value={vehicleInfo.vin} 
          isEditing={isEditing}
          onChange={(v) => setVehicleInfo({...vehicleInfo, vin: v})} 
        />
        
        <Separator className="my-3" />
        
        <div className="grid grid-cols-2 gap-3">
          <EditableField 
            label="Odometer Out" 
            value={vehicleInfo.odometerOut} 
            isEditing={isEditing}
            onChange={(v) => setVehicleInfo({...vehicleInfo, odometerOut: v})} 
            icon={<Gauge className="w-3.5 h-3.5" />} 
          />
          <EditableField 
            label="Odometer In" 
            value={vehicleInfo.odometerIn} 
            isEditing={isEditing}
            onChange={(v) => setVehicleInfo({...vehicleInfo, odometerIn: v})} 
            icon={<Gauge className="w-3.5 h-3.5" />} 
          />
          <EditableField 
            label="Fuel Out" 
            value={vehicleInfo.fuelLevelOut} 
            isEditing={isEditing}
            onChange={(v) => setVehicleInfo({...vehicleInfo, fuelLevelOut: v})} 
          />
          <EditableField 
            label="Fuel In" 
            value={vehicleInfo.fuelLevelIn} 
            isEditing={isEditing}
            onChange={(v) => setVehicleInfo({...vehicleInfo, fuelLevelIn: v})} 
          />
        </div>
      </CardContent>
    </Card>
  );
};

export default VehicleInfoSection;
